import React, { useEffect } from "react";
import {
  ArrowUpRight,
  MapPin,
  Phone,
  Mail,
  Clock,
  PlaneTakeoff,
  Luggage,
  HeartHandshake,
  Activity,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { SERVICES } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import {
  PageHero,
  SectionHeading,
  ServiceCard,
  CTASection,
  ScheduleCallButton,
} from "../components/Shared";

const SERVICES_PHONE = "(604) 996 6862";
const SERVICES_PHONE_HREF = "tel:+16049966862";
const SERVICES_EMAIL = "info@abhifinancial.com";

const SERVICES_MAP_URL =
  "https://www.google.com/maps/dir/?api=1&destination=8128%20128%20St%20%23302%2C%20Surrey%2C%20BC%20V3W%201R1%2C%20Canada";

const SERVICES_TITLE =
  "Insurance Services Surrey BC | Super Visa, Life, Disability | Abhi Insurance";

const SERVICES_META_DESCRIPTION =
  "Super Visa, visitor visa, life, critical illness and disability insurance in Surrey BC. Plans compared across Canada's top insurers. Call (604) 996 6862.";

const SERVICES_DISCLAIMER =
  "Abhi Insurance Agency Inc. is a licensed insurance advisory based in Surrey, British Columbia. The information on this page is general in nature and is not financial, legal or insurance advice. Coverage terms, eligibility, premiums and program requirements vary by insurer and by individual circumstances, and change over time. Nothing here guarantees eligibility, approval or a particular premium. Speak with a licensed advisor before making any insurance decision, and refer to your policy documents for the terms that actually apply to you.";

  const MOBILE_PAGE_DISCLAIMER =
  "This information is general only and is not financial, legal or insurance advice. Coverage, eligibility and premiums vary. Speak with a licensed advisor before making an insurance decision.";

/*
 * All styles below are scoped to the Services page and mobile only.
 * Desktop layout is not changed.
 */
const SERVICES_MOBILE_CSS = `
  @media (max-width: 1023px) {
    /*
     * Finding Your Coverage summary card.
     */
    #abhi-services-page .services-coverage-summary {
      text-align: center !important;
    }

    #abhi-services-page .services-coverage-summary-icon {
      display: block !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }

    /*
     * Coverage guide items.
     */
    #abhi-services-page .services-coverage-item {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
    }

    #abhi-services-page .services-coverage-item-icon {
      margin-left: auto !important;
      margin-right: auto !important;
    }

    #abhi-services-page .services-coverage-item-content {
      width: 100% !important;
      text-align: center !important;
    }

    #abhi-services-page .services-coverage-item-heading {
      justify-content: center !important;
      text-align: center !important;
    }

    /*
     * Hide 01, 02, 03, 04 and 05 on mobile.
     */
    #abhi-services-page .services-coverage-number {
      display: none !important;
    }

    /*
     * Our Location heading alignment.
     */
    #abhi-services-page .services-location-header {
      align-items: center !important;
      text-align: center !important;
    }

    #abhi-services-page .services-location-title {
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
    }

    #abhi-services-page .services-location-agency-name {
      width: 100% !important;
      text-align: center !important;
    }

    /*
     * Keep location details in one column on mobile.
     */
    #abhi-services-page .services-location-details {
      grid-template-columns: minmax(0, 1fr) !important;
    }

    /*
     * Address, phone and email use the same icon/text columns.
     */
    #abhi-services-page .services-location-detail-row {
      display: grid !important;
      grid-template-columns: 40px minmax(0, 1fr) 16px !important;
      align-items: center !important;
      column-gap: 14px !important;
      text-align: left !important;
    }

  #abhi-services-page .services-location-address-copy {
  grid-column: 2 !important;
  text-align: left !important;
}

    #abhi-services-page
      .services-location-detail-row
      > span:nth-child(2) {
      text-align: left !important;
    }

    #abhi-services-page .services-location-detail-row > svg {
      grid-column: 3 !important;
      margin-left: auto !important;
    }

    #abhi-services-page .services-location-hours {
      justify-content: flex-start !important;
      text-align: left !important;
    }
  }
`;

const SERVICES_PAGE_IMAGES = {
  "super-visa-insurance":
    "https://images.pexels.com/photos/3768131/pexels-photo-3768131.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "visitor-visa-insurance":
    "https://images.pexels.com/photos/723240/pexels-photo-723240.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "life-insurance":
    "https://images.pexels.com/photos/1128318/pexels-photo-1128318.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "critical-illness-insurance":
    "https://images.pexels.com/photos/5452201/pexels-photo-5452201.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "disability-insurance":
    "https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=1200",
};

const SERVICE_DESCRIPTIONS = {
  "super-visa-insurance":
    "Mandatory medical coverage for parents and grandparents visiting Canada on the Super Visa program. Minimum $100,000 in coverage, valid at least 12 months, with monthly payment plans and full refunds if the visa is refused.",

  "visitor-visa-insurance":
    "Emergency medical protection for anyone visiting Canada, on a short trip, an extended stay, or while waiting for provincial health coverage to begin.",

  "life-insurance":
    "Term, whole and permanent policies compared side by side, so your family stays in their home and keeps up with everyday expenses if something happens to you.",

  "critical-illness-insurance":
    "A lump sum benefit on diagnosis of a covered illness, paid whether or not you stop working, so you can focus on recovery.",

  "disability-insurance":
    "A monthly benefit replacing part of your income if illness or injury stops you working, with short term and long term options.",
};

const PAGE_SERVICES = SERVICES.map((service) => ({
  ...service,
  short: SERVICE_DESCRIPTIONS[service.slug] ?? service.short,
}));

const COVERAGE_GUIDE = [
  {
    icon: PlaneTakeoff,
    title: "Your parents or grandparents are coming to Canada long term.",
    text: "You need Super Visa insurance. It is a legal requirement of the visa application, not an optional extra.",
  },
  {
    icon: Luggage,
    title: "Someone is visiting for a few weeks or months.",
    text: "Visitor visa insurance covers emergency medical care during the stay.",
  },
  {
    icon: HeartHandshake,
    title: "You have people who depend on your income.",
    text: "Life insurance replaces that income. Term is the cheapest way to cover a fixed period such as the years until a mortgage is paid off. Whole and permanent policies cost more but last for life and build cash value.",
  },
  {
    icon: Activity,
    title: "You are worried about a serious diagnosis.",
    text: "Critical illness insurance pays a lump sum on diagnosis, which you can spend however you need.",
  },
  {
    icon: ShieldCheck,
    title: "You are worried about not being able to work.",
    text: "Disability insurance replaces part of your monthly income while you are unable to earn.",
  },
];

function useServicesMetadata() {
  useEffect(() => {
    const previousTitle = document.title;

    const existingDescription = document.head.querySelector(
      'meta[name="description"]'
    );

    const previousDescription = existingDescription
      ? existingDescription.getAttribute("content")
      : null;

    const description =
      existingDescription || document.createElement("meta");

    if (!existingDescription) {
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    document.title = SERVICES_TITLE;
    description.setAttribute("content", SERVICES_META_DESCRIPTION);

    return () => {
      document.title = previousTitle;

      if (!existingDescription) {
        description.remove();
      } else if (previousDescription === null) {
        description.removeAttribute("content");
      } else {
        description.setAttribute("content", previousDescription);
      }
    };
  }, []);
}

export default function Services() {
  const ref = useRevealGroup();

  useServicesMetadata();

  return (
    <div ref={ref} id="abhi-services-page">
      <style>{SERVICES_MOBILE_CSS}</style>

      {/* HERO */}
      <PageHero
        badge="Our Services"
        title="Reliable Insurance, Anytime, Anywhere"
        subtitle="What type of insurance do you need? Here is a closer look at what our advisors provide and why it might matter to you."
        image="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=2000"
        imageAlt="An insurance advisor helping a client review coverage options"
      />

      {/* SERVICES */}
      <section className="bg-[#F4F7FB] py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            badge="What We Offer"
            title={
              <>
                Coverage For Every{" "}
                <span className="text-[#0785C7]">
                  Stage of Life
                </span>
              </>
            }
            subtitle="If you were born in Canada, if you are new to the country, or if you are just visiting, we will help you find the right coverage for the right price."
          />

          <div className="mt-12 grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {PAGE_SERVICES.map((service, index) => (
              <ServiceCard
                key={service.slug}
                service={service}
                index={index}
                image={SERVICES_PAGE_IMAGES[service.slug]}
              />
            ))}

            {/* Inline CTA */}
            <div
              data-reveal
              style={{ transitionDelay: "400ms" }}
              className="group relative flex min-h-[350px] flex-col justify-between overflow-hidden rounded-2xl bg-[#006FB9] p-8 text-white shadow-[0_2px_16px_rgba(0,111,185,0.15)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_48px_rgba(0,111,185,0.3)]"
            >
              <div
                aria-hidden="true"
                className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#B9DFF3]/15 transition-transform duration-700 group-hover:scale-150"
              />

              <div className="relative">
                <h3 className="font-heading text-2xl font-bold leading-tight">
                  Not sure which type of insurance you need?
                </h3>

                <p className="mt-4 text-sm leading-relaxed text-[#C5DEEC]">
                  Book a free consultation. Call{" "}
                  <a
                    href={SERVICES_PHONE_HREF}
                    className="font-semibold text-white hover:underline"
                  >
                    {SERVICES_PHONE}
                  </a>
                  .
                </p>
              </div>

              <ScheduleCallButton
                className="btn-light relative mt-8 w-fit"
                testId="services-call-btn"
              >
                Book a Free Consultation
              </ScheduleCallButton>
            </div>
          </div>
        </div>
      </section>

      {/* COVERAGE GUIDE */}
      <section
        className="bg-white py-16 lg:py-20"
        aria-labelledby="services-coverage-heading"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start lg:gap-16">
            <div data-reveal className="lg:sticky lg:top-28">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#E2F2FA] px-4 py-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-[#006FB9]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#006FB9]" />
                Finding Your Coverage
              </span>

              <h2
                id="services-coverage-heading"
                className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl lg:text-[44px]"
              >
                Which coverage do you{" "}
                <span className="text-[#0785C7]">
                  actually need?
                </span>
              </h2>

              <div className="services-coverage-summary mt-7 overflow-hidden rounded-2xl bg-[#006FB9] p-6 text-white shadow-[0_16px_36px_rgba(0,111,185,0.18)] sm:p-7">
                <CheckCircle2 className="services-coverage-summary-icon h-7 w-7 text-[#B9DFF3]" />

                <p className="mt-4 font-heading text-xl font-bold leading-snug">
                  Most clients need more than one.
                </p>

                <p className="mt-3 text-sm leading-relaxed text-[#D4E9F4]">
                  We will tell you honestly which ones matter for your
                  situation and which ones do not.
                </p>
              </div>
            </div>

            <div
              data-reveal
              style={{ transitionDelay: "120ms" }}
              className="overflow-hidden rounded-3xl border border-[#DCEAF2] bg-white shadow-[0_16px_45px_rgba(7,59,103,0.09)]"
            >
              {COVERAGE_GUIDE.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    data-reveal
                    style={{ transitionDelay: `${index * 70}ms` }}
                    className={[
                      "services-coverage-item",
                      "group grid grid-cols-[auto_1fr] gap-4 p-6",
                      "transition-colors duration-300 hover:bg-[#F8FCFE]",
                      "sm:gap-5 sm:p-7",
                      index !== COVERAGE_GUIDE.length - 1
                        ? "border-b border-[#E1ECF3]"
                        : "",
                    ].join(" ")}
                  >
                    <span className="services-coverage-item-icon grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#E2F2FA] transition-all duration-300 group-hover:bg-[#006FB9]">
                      <Icon className="h-5 w-5 text-[#006FB9] transition-colors duration-300 group-hover:text-white" />
                    </span>

                    <div className="services-coverage-item-content min-w-0">
                      <div className="services-coverage-item-heading flex items-start gap-3">
                        <span className="services-coverage-number mt-0.5 font-heading text-xs font-extrabold text-[#0785C7]">
                          0{index + 1}
                        </span>

                        <h3 className="font-heading text-lg font-bold leading-snug text-[#0B2748] sm:text-xl">
                          {item.title}
                        </h3>
                      </div>

                      <p className="mt-3 text-sm leading-relaxed text-[#526A7D] sm:text-[15px]">
                        {item.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* OUR LOCATION */}
      <section
        className="bg-white py-16 lg:py-20"
        aria-labelledby="services-location-heading"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div
            data-reveal
            className="overflow-hidden rounded-3xl border border-[#E1ECF3] bg-white shadow-[0_12px_36px_rgba(7,59,103,0.08)]"
          >
            {/* Heading and company name */}
            <div className="services-location-header flex flex-col gap-5 border-b border-[#E1ECF3] bg-[#F4F7FB] px-6 py-6 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="services-location-title flex items-center gap-4">
                {/* <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#006FB9]">
                  <MapPin
                    aria-hidden="true"
                    className="h-6 w-6 text-white"
                  />
                </span> */}

                <h2
                  id="services-location-heading"
                  className="font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl"
                >
                  Our{" "}
                  <span className="text-[#0785C7]">
                    Location
                  </span>
                </h2>
              </div>

              <h3 className="services-location-agency-name font-heading text-lg font-bold text-[#0B2748] lg:text-right">
                Abhi Insurance Agency Inc.
              </h3>
            </div>

            {/* Address, phone and email */}
            <address className="services-location-details grid grid-cols-1 not-italic md:grid-cols-3">
            <a
  href={SERVICES_MAP_URL}
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Open Abhi Insurance Agency location in Google Maps"
  className="services-location-detail-row group flex min-w-0 items-center gap-3 px-6 py-6 transition-colors hover:bg-[#F4F7FB] sm:px-8"
>
  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#E2F2FA] transition-colors group-hover:bg-[#006FB9]">
    <MapPin
      aria-hidden="true"
      className="h-5 w-5 text-[#006FB9] transition-colors group-hover:text-white"
    />
  </span>

  <span className="services-location-address-copy min-w-0 text-[15px] font-medium leading-relaxed text-[#193B58]">
    8128 128 St #302

    <span className="block font-normal text-[#526A7D]">
      Surrey, BC V3W 1R1
    </span>
  </span>

  <ArrowUpRight
    aria-hidden="true"
    className="ml-auto h-4 w-4 shrink-0 text-[#006FB9]"
  />
</a>

              <a
                href={SERVICES_PHONE_HREF}
                className="services-location-detail-row group flex min-w-0 items-center gap-3 border-t border-[#E1ECF3] px-6 py-6 transition-colors hover:bg-[#F4F7FB] sm:px-8 md:border-l md:border-t-0"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#E2F2FA] transition-colors group-hover:bg-[#006FB9]">
                  <Phone
                    aria-hidden="true"
                    className="h-5 w-5 text-[#006FB9] transition-colors group-hover:text-white"
                  />
                </span>

                <span className="min-w-0">
                  <span className="block text-xs font-medium text-[#526A7D]">
                    Phone:
                  </span>

                  <span className="mt-1 block text-[15px] font-semibold text-[#006FB9] group-hover:underline">
                    {SERVICES_PHONE}
                  </span>
                </span>

                <ArrowUpRight
                  aria-hidden="true"
                  className="ml-auto h-4 w-4 shrink-0 text-[#006FB9]"
                />
              </a>

              <a
                href={`mailto:${SERVICES_EMAIL}`}
                className="services-location-detail-row group flex min-w-0 items-center gap-3 border-t border-[#E1ECF3] px-6 py-6 transition-colors hover:bg-[#F4F7FB] sm:px-8 md:border-l md:border-t-0"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#E2F2FA] transition-colors group-hover:bg-[#006FB9]">
                  <Mail
                    aria-hidden="true"
                    className="h-5 w-5 text-[#006FB9] transition-colors group-hover:text-white"
                  />
                </span>

                <span className="min-w-0">
                  <span className="block text-xs font-medium text-[#526A7D]">
                    Email:
                  </span>

                  <span className="mt-1 block break-words text-[15px] font-semibold text-[#006FB9] group-hover:underline">
                    {SERVICES_EMAIL}
                  </span>
                </span>

                <ArrowUpRight
                  aria-hidden="true"
                  className="ml-auto h-4 w-4 shrink-0 text-[#006FB9]"
                />
              </a>
            </address>

            {/* Working hours */}
            <div className="services-location-hours flex items-center gap-3 bg-[#073B67] px-6 py-4 text-[#D4E7F1] sm:px-8">
              <Clock
                aria-hidden="true"
                className="h-5 w-5 shrink-0 text-[#B9DFF3]"
              />

              <p className="text-sm font-medium leading-relaxed">
                Open 7 days a week, 9:00 to 17:00
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <CTASection
        title="Get Matched With the Right Policy Today"
        subtitle="Free consultations. No hidden fees. Your satisfaction is 100% guaranteed."
      />

{/* SERVICE AND PAGE DISCLAIMER */}
<section
  aria-label="Service and page disclaimer"
  className="bg-[#F4F7FB] py-8 lg:py-10"
>
  <div className="mx-auto max-w-7xl px-6">
    <div className="rounded-xl border-l-[5px] border-r-[5px] border-[#0B4FAF] bg-white px-5 py-5 shadow-[0_8px_24px_rgba(11,39,72,0.08)] sm:px-7 lg:px-8">
      
      {/* Short disclaimer for mobile */}
      <p className="text-sm leading-relaxed text-[#526A7D] md:hidden">
        {MOBILE_PAGE_DISCLAIMER}
      </p>

      {/* Complete disclaimer for tablet and desktop */}
      <p className="hidden text-sm leading-relaxed text-[#526A7D] md:block">
        {SERVICES_DISCLAIMER}
      </p>
      
    </div>
  </div>
</section>
    </div>
  );
}